import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import MarketingEmailSettings from '../components/MarketingEmailSettings';
import {
  Mail, Users, Send, Eye, MousePointer, TrendingUp, TrendingDown,
  BarChart3, PieChart, RefreshCw, Plus, ArrowRight, FileText,
  List, Filter, Target, Percent, Clock, CheckCircle, AlertCircle, Settings
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MarketingDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState('30');
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const token = sessionStorage.getItem('auth_token');

  useEffect(() => {
    if (token) {
      fetchDashboardStats();
    }
  }, [token, days]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/marketing/analytics/dashboard?days=${days}&token=${token}`
      );
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatPercent = (num) => {
    return (num || 0).toFixed(1) + '%';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const campaignStats = stats?.campaigns || {};
  const emailStats = campaignStats?.emails || {};
  const contactGrowth = stats?.contact_growth || {};
  const totals = stats?.totals || {};

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="marketing-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing Dashboard</h1>
          <p className="text-gray-500 mt-1">
            Email campaigns, audience insights, and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowEmailSettings(true)} data-testid="email-settings-btn">
            <Settings className="h-4 w-4 mr-2" />
            Email Settings
          </Button>
          <Button onClick={() => navigate('/marketing/campaigns/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Sent</p>
                <p className="text-2xl font-bold">{formatNumber(emailStats.total_sent)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Send className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Open Rate</p>
                <p className="text-2xl font-bold">{formatPercent(emailStats.open_rate)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Eye className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Click Rate</p>
                <p className="text-2xl font-bold">{formatPercent(emailStats.click_rate)}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <MousePointer className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Delivery Rate</p>
                <p className="text-2xl font-bold">{formatPercent(emailStats.delivery_rate)}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email Performance Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Email Performance</CardTitle>
            <CardDescription>Opens, clicks, and bounces over time</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.email_performance?.daily_stats?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={stats.email_performance.daily_stats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    labelFormatter={(val) => new Date(val).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="sent" stroke="#3b82f6" fill="#93c5fd" name="Sent" />
                  <Area type="monotone" dataKey="opened" stroke="#22c55e" fill="#86efac" name="Opened" />
                  <Area type="monotone" dataKey="clicked" stroke="#a855f7" fill="#d8b4fe" name="Clicked" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-gray-400">
                <BarChart3 className="h-12 w-12 mb-2" />
                <p>No email data for this period</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-between"
              onClick={() => navigate('/marketing/campaigns/new')}
            >
              <span className="flex items-center">
                <Mail className="h-4 w-4 mr-2" />
                Create Campaign
              </span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-between"
              onClick={() => navigate('/marketing/lists')}
            >
              <span className="flex items-center">
                <List className="h-4 w-4 mr-2" />
                Manage Lists
              </span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-between"
              onClick={() => navigate('/marketing/segments')}
            >
              <span className="flex items-center">
                <Filter className="h-4 w-4 mr-2" />
                Build Segment
              </span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-between"
              onClick={() => navigate('/marketing/templates')}
            >
              <span className="flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Email Templates
              </span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaign Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Campaign Summary</CardTitle>
            <CardDescription>Last {days} days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Total Campaigns</span>
                <span className="font-semibold">{campaignStats.total || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center text-gray-500">
                  <Badge variant="default" className="mr-2 bg-green-500">Sent</Badge>
                </span>
                <span className="font-semibold">{campaignStats.by_status?.sent || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center text-gray-500">
                  <Badge variant="outline" className="mr-2">Draft</Badge>
                </span>
                <span className="font-semibold">{campaignStats.by_status?.draft || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center text-gray-500">
                  <Badge variant="secondary" className="mr-2">Scheduled</Badge>
                </span>
                <span className="font-semibold">{campaignStats.by_status?.scheduled || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Growth */}
        <Card>
          <CardHeader>
            <CardTitle>Audience</CardTitle>
            <CardDescription>Contacts in marketing lists</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center mb-4">
              <p className="text-4xl font-bold text-gray-900">
                {formatNumber(contactGrowth.total_in_lists)}
              </p>
              <p className="text-sm text-gray-500">Total Subscribers</p>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="text-center">
                <p className="text-lg font-semibold text-green-600">
                  +{formatNumber(contactGrowth.new_in_period)}
                </p>
                <p className="text-xs text-gray-500">New</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-red-600">
                  -{formatNumber(contactGrowth.unsubscribes_in_period)}
                </p>
                <p className="text-xs text-gray-500">Unsubscribed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Asset Counts */}
        <Card>
          <CardHeader>
            <CardTitle>Marketing Assets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div 
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                onClick={() => navigate('/marketing/lists')}
              >
                <span className="flex items-center text-gray-700">
                  <List className="h-5 w-5 mr-3 text-blue-500" />
                  Contact Lists
                </span>
                <span className="font-semibold">{totals.lists || 0}</span>
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                onClick={() => navigate('/marketing/segments')}
              >
                <span className="flex items-center text-gray-700">
                  <Filter className="h-5 w-5 mr-3 text-purple-500" />
                  Segments
                </span>
                <span className="font-semibold">{totals.segments || 0}</span>
              </div>
              <div 
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                onClick={() => navigate('/marketing/templates')}
              >
                <span className="flex items-center text-gray-700">
                  <FileText className="h-5 w-5 mr-3 text-amber-500" />
                  Email Templates
                </span>
                <span className="font-semibold">{totals.templates || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Performing Campaigns */}
      {stats?.email_performance?.top_campaigns?.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Top Performing Campaigns</CardTitle>
                <CardDescription>By open count</CardDescription>
              </div>
              <Button variant="outline" onClick={() => navigate('/marketing/campaigns')}>
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Campaign</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Sent</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Opened</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Clicked</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Open Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.email_performance.top_campaigns.map((campaign) => {
                    const sent = campaign.stats?.sent || 0;
                    const opened = campaign.stats?.opened || 0;
                    const clicked = campaign.stats?.clicked || 0;
                    const openRate = sent > 0 ? ((opened / sent) * 100).toFixed(1) : '0';
                    
                    return (
                      <tr 
                        key={campaign.id} 
                        className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}
                      >
                        <td className="py-3 px-4">
                          <p className="font-medium text-gray-900">{campaign.name}</p>
                        </td>
                        <td className="py-3 px-4 text-right text-gray-600">{formatNumber(sent)}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{formatNumber(opened)}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{formatNumber(clicked)}</td>
                        <td className="py-3 px-4 text-right">
                          <Badge variant={parseFloat(openRate) > 20 ? "default" : "secondary"}>
                            {openRate}%
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Settings Dialog */}
      <MarketingEmailSettings 
        open={showEmailSettings} 
        onOpenChange={setShowEmailSettings} 
      />
    </div>
  );
}
